import React from 'react';
import useDeploySummary from 'Hooks/query-hooks/deployment/useDeploySummary';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useTranslation } from 'react-i18next';
import useIsDeploying from 'Hooks/useIsDeploying';
import DeployModalConfirm from './DeployModalConfirm';
import DeployModalLoading from './DeployModalLoading';
import NothingToDeployModal from './NothingToDeployModal';

interface Props {
    closeModal(): void;
    rulesType?: RulesType;
}

const DeployModalContent = ({ closeModal, rulesType }: Props) => {
    const { isFetching, isError, data: deploymentSummary } = useDeploySummary();
    const hasError = Boolean(!isFetching && (isError || !deploymentSummary));
    const { addModal, addTechnicalErrorModal } = useReduxModal();
    const { t } = useTranslation();
    const isDeploying = useIsDeploying(true);

    React.useEffect(() => {
        if (isDeploying) {
            addModal({
                id: 'deploy-warning',
                status: 'warning',
                title: t('Deployment is in progress'),
                content: t('A deployment is currently in progress'),
                cancelText: t('Close')
            });
            closeModal();
        }
    }, [isDeploying, addModal, t, closeModal]);

    React.useEffect(() => {
        if (hasError) {
            closeModal();
            addTechnicalErrorModal(t('Failed to retrieve deployment summary'), 'Unable to retrieve changes');
        }
    }, [hasError, closeModal, addTechnicalErrorModal, t]);

    if (isFetching) {
        return <DeployModalLoading closeModal={closeModal} />;
    }

    if (!isFetching && deploymentSummary) {
        const deployCount = deploymentSummary.totalUpdates || 0;

        if (deployCount) {
            return (
                <DeployModalConfirm
                    closeModal={closeModal}
                    deploymentSummary={deploymentSummary}
                    rulesType={rulesType}
                />
            );
        }

        return <NothingToDeployModal closeModal={closeModal} />;
    }
    return null;
};

export default DeployModalContent;
